import React, { useState, useEffect } from 'react';
import { getOnboardingDetail, approveOnboarding } from '../../services/platformApi';
import './AdminOnboarding.css';

const OnboardingDetail = ({ requestId, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(false);
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [assignedEmployeeId, setAssignedEmployeeId] = useState('');

  // Document preview modal
  const [preview, setPreview] = useState({ open: false, title: '', url: '', isPdf: false });

  const [approvalData, setApprovalData] = useState({
    employeeId: '',
    role: 'EMPLOYEE',
    managerName: '',
  });
  const [approvalErrors, setApprovalErrors] = useState({});

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line
  }, [requestId]);

  // Close preview on Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') closePreview();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getOnboardingDetail(requestId);
      // Backend returns { success: true, data: {...} } — unwrap `data`.
      const payload = res && res.data ? res.data : res;
      setDetail(payload || {});

      const suggested =
        (payload && (payload.suggestedEmployeeId || payload.suggested_employee_id)) || '';
      if (suggested) {
        setApprovalData((prev) => ({ ...prev, employeeId: suggested }));
      }
    } catch (err) {
      setError(err.message || 'Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  // Normalize (handles camelCase and snake_case)
  const d = detail
    ? {
        fullName: detail.fullName || detail.full_name || '-',
        companyEmail: detail.email || detail.companyEmail || detail.company_email || '-',
        personalEmail: detail.personalEmail || detail.personal_email || '',
        phone: detail.phone || '',
        dateOfBirth: detail.dateOfBirth || detail.date_of_birth || '',
        gender: detail.gender || '',
        bloodGroup: detail.bloodGroup || detail.blood_group || '',
        currentAddress: detail.currentAddress || detail.current_address || '',
        currentCity: detail.currentCity || detail.current_city || '',
        currentState: detail.currentState || detail.current_state || '',
        currentPincode: detail.currentPincode || detail.current_pincode || '',
        permanentAddress: detail.permanentAddress || detail.permanent_address || '',
        permanentCity: detail.permanentCity || detail.permanent_city || '',
        permanentState: detail.permanentState || detail.permanent_state || '',
        permanentPincode: detail.permanentPincode || detail.permanent_pincode || '',
        department: detail.department || '',
        designation: detail.designation || '',
        emergencyContactName: detail.emergencyContactName || detail.emergency_contact_name || '',
        emergencyContactPhone: detail.emergencyContactPhone || detail.emergency_contact_phone || '',
        requestedApps: detail.requestedApps || detail.requested_apps || [],
        documents: detail.documents || {},
        availableApps: detail.availableApps || detail.available_apps || [],
        status: detail.status || 'PENDING',
        employeeId: detail.employeeId || detail.employee_id || '',
        role: detail.role || '',
        managerName: detail.managerName || detail.manager_name || '',
        approvedAt: detail.approvedAt || detail.approved_at || '',
      }
    : null;

  // Resolve a document URL from any of the possible key names
  const docUrl = (docs, type) =>
    docs[type] || docs[`${type}Url`] || docs[`${type}_url`] || '';

  const isPdfUrl = (url) => /\.pdf(\?|$)/i.test(url) || /pdf/i.test(url);

  const openPreview = (title, url) => {
    if (!url) return;
    setPreview({ open: true, title, url, isPdf: isPdfUrl(url) });
  };
  const closePreview = () => setPreview({ open: false, title: '', url: '', isPdf: false });

  const handleApprovalChange = (field, value) => {
    setApprovalData((prev) => ({ ...prev, [field]: value }));
    if (approvalErrors[field]) {
      setApprovalErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };



  const validateApproval = () => {
    const errors = {};
    const eid = (approvalData.employeeId || '').trim();
    if (!eid) {
      errors.employeeId = 'Employee ID is required';
    } else if (!/^[A-Za-z0-9-]{3,20}$/.test(eid)) {
      errors.employeeId = 'Use 3-20 chars: letters, numbers, hyphen (e.g. CTO-001)';
    }
    if (!approvalData.role) errors.role = 'Role is required';
    setApprovalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleApprove = async () => {
    if (!validateApproval()) return;
    setApproving(true);
    try {
      const res = await approveOnboarding(requestId, {
        action: 'approve',
        role: approvalData.role,
        managerId: null,
        employeeId: approvalData.employeeId.trim().toUpperCase(),
      });
      const eid =
        (res && (res.employeeId || res.employee_id)) ||
        (res && res.data && (res.data.employeeId || res.data.employee_id)) ||
        approvalData.employeeId.trim().toUpperCase();
      setAssignedEmployeeId(eid);
      setApproveSuccess(true);
    } catch (err) {
      setApprovalErrors({ submit: err.message || 'Approval failed. Please try again.' });
    } finally {
      setApproving(false);
    }
  };

  const initials = (name) => {
    if (!name || name === '-') return 'U';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };

  // ---------- Loading / Error / Success states ----------
  if (loading) {
    return (
      <div className="odx-state">
        <div className="odx-spinner" />
        <span className="odx-state__text">Loading request details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="odx-state">
        <div className="odx-state__emoji">⚠️</div>
        <p className="odx-state__err">{error}</p>
        <button className="odx-btn-ghost" onClick={onBack}>← Back to List</button>
      </div>
    );
  }

  if (approveSuccess) {
    return (
      <div className="odx-state">
        <div className="odx-success">✓</div>
        <h2 className="odx-success__title">Employee Approved!</h2>
        <p className="odx-success__msg">
          <strong>{d.fullName}</strong> has been activated with Employee ID{' '}
          <strong className="odx-success__id">{assignedEmployeeId}</strong>.
        </p>
        <p className="odx-success__sub">They will see the dashboard on their next login.</p>
        <button className="odx-btn-primary" onClick={onBack}>← Back to Requests</button>
      </div>
    );
  }

  const aadhar = docUrl(d.documents, 'aadhar');
  const pan = docUrl(d.documents, 'pan');
  const photo = docUrl(d.documents, 'photo');
  const noDocs = !aadhar && !pan && !photo;
  const docCount = [aadhar, pan, photo].filter(Boolean).length;

  const statusKey = (d.status || 'PENDING').toUpperCase();
  const statusClass =
    statusKey === 'ACTIVE' || statusKey === 'APPROVED'
      ? 'odx-status--ok'
      : statusKey === 'REJECTED'
      ? 'odx-status--rej'
      : 'odx-status--pend';

  const isApproved = d.status === 'APPROVED' || d.status === 'ACTIVE';

  return (
    <div className="odx">
      {/* Top bar */}
      <div className="odx-topbar">
        <button className="odx-back" onClick={onBack}>
          <Icon.Arrow /> Back to Requests
        </button>
        <span className={`odx-status ${statusClass}`}>
          <i className="odx-status__dot" /> {statusKey}
        </span>
      </div>

      {/* ===== ONE unified bento container ===== */}
      <div className="odx-bento">
        {/* hero */}
        <div className="odx-hero">
          <div className="odx-hero__glow" />
          <div
            className={`odx-hero__av ${photo ? 'odx-hero__av--click' : ''}`}
            onClick={() => photo && openPreview('Passport Photo', photo)}
          >
            {photo ? <img src={photo} alt={d.fullName} /> : <span>{initials(d.fullName)}</span>}
          </div>
          <div className="odx-hero__info">
            <h1 className="odx-hero__name">{d.fullName}</h1>
            <div className="odx-hero__email">{d.companyEmail}</div>
            <div className="odx-hero__chips">
              {d.designation && <span className="odx-hchip">{d.designation}</span>}
              {d.department && <span className="odx-hchip">{d.department}</span>}
            </div>
          </div>
        </div>

        {/* stat strip — 3 boxes divided by lines */}
        <div className="odx-stats">
          <Stat tone="teal" icon={<Icon.Badge />} value={d.employeeId || (isApproved ? '-' : 'Pending')} label="Employee ID" />
          <Stat tone="navy" icon={<Icon.Work />} value={d.department || '-'} label="Department" />
          <Stat tone="green" icon={<Icon.Check />} value={isApproved ? (d.role || '-') : statusKey} label={isApproved ? 'Role' : 'Status'} last />
        </div>

        {/* content boxes: divided by lines, zero gap */}
        <div className="odx-body">
          {/* Personal */}
          <Sec tone="navy" icon={<Icon.User />} title="Personal Details">
            <div className="odx-fields">
              <Field label="Full Name" value={d.fullName} />
              <Field label="Gender" value={d.gender || '-'} />
              <Field label="Date of Birth" value={d.dateOfBirth || '-'} />
              <Field label="Blood Group" value={d.bloodGroup || '-'} />
              <Field full label="Company Email" value={d.companyEmail} />
              <Field full label="Personal Email" value={d.personalEmail || '-'} />
              <Field full label="Phone" value={d.phone ? `+91 ${d.phone}` : '-'} />
            </div>
          </Sec>

          {/* Work */}
          <Sec tone="violet" icon={<Icon.Work />} title="Work Details" even>
            <div className="odx-fields">
              <Field label="Department" value={d.department || '-'} />
              <Field label="Designation" value={d.designation || '-'} />
              <Field label="Emergency Contact" value={d.emergencyContactName || '-'} />
              <Field label="Emergency Phone" value={d.emergencyContactPhone ? `+91 ${d.emergencyContactPhone}` : '-'} />
              {d.requestedApps && d.requestedApps.length > 0 && (
                <div className="odx-field odx-field--full">
                  <span className="odx-fl">Requested Apps</span>
                  <div className="odx-tags">
                    {d.requestedApps.map((app, i) => (
                      <span key={i} className="odx-tag">{app.appName || app.name || app.app_key || app}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Sec>

          {/* Address (full) */}
          <Sec tone="teal" icon={<Icon.Home />} title="Address" full>
            <div className="odx-fields">
              <Field full label="Current Address" value={joinAddr(d.currentAddress, d.currentCity, d.currentState, d.currentPincode)} />
              <Field full label="Permanent Address" value={joinAddr(d.permanentAddress, d.permanentCity, d.permanentState, d.permanentPincode)} />
            </div>
          </Sec>

          {/* Documents (full) */}
          <Sec tone="amber" icon={<Icon.Doc />} title="Documents" count={noDocs ? null : `${docCount} files`} full>
            {noDocs ? (
              <p className="odx-muted">No documents available.</p>
            ) : (
              <div className="odx-docgrid">
                <DocTile label="Aadhar Card" url={aadhar} kind="pdf" onView={() => openPreview('Aadhar Card', aadhar)} />
                <DocTile label="PAN Card" url={pan} kind="pdf" onView={() => openPreview('PAN Card', pan)} />
                <DocTile label="Passport Photo" url={photo} kind="img" onView={() => openPreview('Passport Photo', photo)} />
              </div>
            )}
          </Sec>

          {/* Approval / Approved (full, last) — NO grant app access */}
          {d.status === 'PENDING' ? (
            <Sec tone="green" icon={<Icon.Check />} title="Approve Employee" full last>
              <div className="odx-approw">
                <div className="odx-fieldwrap">
                  <label className="odx-lbl">Employee ID <span className="odx-req">*</span></label>
                  <input
                    type="text"
                    className={`odx-inp ${approvalErrors.employeeId ? 'odx-inp--err' : ''}`}
                    placeholder="e.g., CTO-001"
                    value={approvalData.employeeId}
                    maxLength={20}
                    onChange={(e) => handleApprovalChange('employeeId', e.target.value.toUpperCase())}
                  />
                  <span className="odx-hint">Type the Employee ID manually.</span>
                  {approvalErrors.employeeId && <span className="odx-err">{approvalErrors.employeeId}</span>}
                </div>
                <div className="odx-fieldwrap">
                  <label className="odx-lbl">Role <span className="odx-req">*</span></label>
                  <select
                    className={`odx-inp ${approvalErrors.role ? 'odx-inp--err' : ''}`}
                    value={approvalData.role}
                    onChange={(e) => handleApprovalChange('role', e.target.value)}
                  >
                    <option value="EMPLOYEE">Employee</option>
                    <option value="MANAGER">Manager</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                  {approvalErrors.role && <span className="odx-err">{approvalErrors.role}</span>}
                </div>
                <div className="odx-fieldwrap odx-fieldwrap--full">
                  <label className="odx-lbl">Manager Name</label>
                  <input
                    type="text"
                    className="odx-inp"
                    placeholder="Reporting manager (optional)"
                    value={approvalData.managerName}
                    onChange={(e) => handleApprovalChange('managerName', e.target.value)}
                  />
                </div>
              </div>
              {approvalErrors.submit && <div className="odx-alert">⚠️ {approvalErrors.submit}</div>}
              <button className="odx-approve" onClick={handleApprove} disabled={approving}>
                {approving ? 'Approving...' : <><Icon.Check /> Approve &amp; Activate</>}
              </button>
            </Sec>
          ) : (
            <Sec tone="green" icon={<Icon.Check />} title="Approved" full last>
              <div className="odx-fields">
                <Field label="Employee ID" value={d.employeeId || '-'} highlight />
                <Field label="Role" value={d.role || '-'} />
                <Field label="Manager" value={d.managerName || '-'} />
                <Field label="Approved On" value={d.approvedAt || '-'} />
              </div>
            </Sec>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {preview.open && (
        <div className="odx-modal" onClick={closePreview}>
          <div className="odx-modal__box" onClick={(e) => e.stopPropagation()}>
            <div className="odx-modal__head">
              <h4 className="odx-modal__title">{preview.title}</h4>
              <div className="odx-modal__actions">
                <a href={preview.url} target="_blank" rel="noopener noreferrer" className="odx-modal__open">Open in new tab ↗</a>
                <button className="odx-modal__close" onClick={closePreview}>×</button>
              </div>
            </div>
            <div className="odx-modal__body">
              {preview.isPdf ? (
                <iframe title={preview.title} src={preview.url} className="odx-modal__pdf" />
              ) : (
                <img src={preview.url} alt={preview.title} className="odx-modal__img" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ---- Presentational helpers ---- */
function Stat({ tone, icon, value, label, last }) {
  return (
    <div className={`odx-stat ${last ? 'odx-stat--last' : ''}`}>
      <span className={`odx-stat__ic odx-tone-${tone}`}>{icon}</span>
      <div>
        <div className="odx-stat__v">{value}</div>
        <div className="odx-stat__l">{label}</div>
      </div>
    </div>
  );
}

function Sec({ tone, icon, title, count, children, full, even, last }) {
  const cls = [
    'odx-sec',
    full ? 'odx-sec--full' : '',
    even ? 'odx-sec--even' : '',
    last ? 'odx-sec--last' : '',
  ].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <div className="odx-ct">
        <span className={`odx-ct__ic odx-tone-${tone}`}>{icon}</span>
        <h3 className="odx-ct__title">{title}</h3>
        {count && <span className="odx-cnt">{count}</span>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, full, highlight }) {
  return (
    <div className={`odx-field ${full ? 'odx-field--full' : ''}`}>
      <span className="odx-fl">{label}</span>
      <span className={`odx-fv ${highlight ? 'odx-fv--hl' : ''}`}>{value}</span>
    </div>
  );
}

function DocTile({ label, url, kind, onView }) {
  if (!url) return null;
  return (
    <div className="odx-doc" onClick={onView}>
      <div className={`odx-doc__top odx-doc__top--${kind}`}>
        {kind === 'pdf' ? <span className="odx-doc__pdf">PDF</span> : <Icon.Image />}
      </div>
      <div className="odx-doc__b">
        <div className="odx-doc__l">{label}</div>
        <div className="odx-doc__v">Click to preview</div>
      </div>
    </div>
  );
}

function joinAddr(a, c, s, p) {
  const line = [a, c, s].filter(Boolean).join(', ');
  return line ? (p ? `${line} - ${p}` : line) : '-';
}

/* ---- Inline SVG icon set ---- */
const svgBase = { width: 17, height: 17, fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const Icon = {
  Arrow: () => (<svg viewBox="0 0 24 24" style={{ ...svgBase, width: 15, height: 15 }}><path d="M19 12H5M12 19l-7-7 7-7" /></svg>),
  Badge: () => (<svg viewBox="0 0 24 24" style={{ ...svgBase, width: 19, height: 19 }}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>),
  User: () => (<svg viewBox="0 0 24 24" style={svgBase}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0112 0v1" /></svg>),
  Home: () => (<svg viewBox="0 0 24 24" style={svgBase}><path d="M3 10l9-7 9 7v10a1 1 0 01-1 1h-5v-6H9v6H4a1 1 0 01-1-1z" /></svg>),
  Work: () => (<svg viewBox="0 0 24 24" style={svgBase}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>),
  Doc: () => (<svg viewBox="0 0 24 24" style={svgBase}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>),
  Image: () => (<svg viewBox="0 0 24 24" style={{ ...svgBase, width: 25, height: 25 }}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>),
  Check: () => (<svg viewBox="0 0 24 24" style={{ ...svgBase, width: 17, height: 17, strokeWidth: 2.4 }}><path d="M20 6L9 17l-5-5" /></svg>),
};

export default OnboardingDetail;
